import {type Active} from '@dnd-kit/core';
import React from 'react';
import {type VirtuosoHandle} from 'react-virtuoso';

import {type SectionWithRef} from '../../types';

export const handleSectionDragStart = ({
  active,
  visibleSections,
  virtuosoRef,
  scrollAnimationFrameRef,
  visibleRangeRef,
}: {
  active: Active;
  visibleSections: SectionWithRef[];
  virtuosoRef: React.RefObject<VirtuosoHandle>;
  scrollAnimationFrameRef: React.MutableRefObject<number | null>;
  visibleRangeRef: React.RefObject<{
    startIndex: number;
    endIndex: number;
  } | null>;
}) => {
  const currSectionIndex = active.data.current?.sortable?.index;
  if (currSectionIndex == null || currSectionIndex > visibleSections.length) {
    console.error('handleSectionDragStart: Invalid section index', {
      currSectionIndex,
      visibleSections,
    });
    return;
  }

  // If there are any expanded sections above the current section, the page will jump
  // when we collapse all sections for dragging. This helps indicate whether or not
  // we need to scroll upwards to account for this.
  const hasExpandedSectionsAbove = visibleSections
    .slice(0, currSectionIndex)
    .some(section => section.isOpen);

  // Only scroll if the item is not fully visible
  if (virtuosoRef.current && hasExpandedSectionsAbove) {
    // Cancel any pending scroll animation
    if (scrollAnimationFrameRef.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
    }

    scrollAnimationFrameRef.current = requestAnimationFrame(() => {
      // We scroll to the start index of the visible range to account for the collapsed sections above.
      // It's not perfect because the mouse cursor might be in a different position after the shift, but
      // the range of sections will be visible. Worst case, users can cancel the drag operation using ESC.
      if (virtuosoRef.current && visibleRangeRef.current?.startIndex != null) {
        virtuosoRef.current.scrollToIndex({
          index: visibleRangeRef.current.startIndex,
          align: 'start',
          behavior: 'auto',
        });
      }
      // Clear the ref after execution
      scrollAnimationFrameRef.current = null;
    });
  }
};
